/**
 * Google Analytics 4 (gtag.js) with Consent Mode v2.
 *
 * Rules:
 * - Consent defaults to DENIED before anything loads (GDPR / ePrivacy).
 * - The gtag.js script is only injected AFTER analytics consent is granted.
 * - Consent changes are pushed to GA with `consent: update` — no reload needed.
 * - Without a measurement ID every function is a no-op (safe for dev/preview).
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID: string | undefined =
  import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY;

let defaultsSet = false;
let scriptInjected = false;
let configured = false;

/**
 * gtag.js only executes dataLayer entries that are real `arguments` objects —
 * plain arrays are silently ignored, which means no hits are ever sent.
 * Declaring the classic `function gtag(){ dataLayer.push(arguments) }` wrapper
 * (never an arrow function) is what produces the correct shape.
 */
function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const pushToDataLayer = (...args: unknown[]) => {
  window.gtag = window.gtag ?? (gtag as (...a: unknown[]) => void);
  (window.gtag as (...a: unknown[]) => void)(...args);
};


/** True when a GA4 measurement ID is configured for this environment. */
export const isAnalyticsConfigured = () => Boolean(MEASUREMENT_ID);

/**
 * Sets Consent Mode v2 defaults to denied. Must run before gtag.js loads.
 * Safe to call multiple times.
 */
export const initConsentDefaults = () => {
  if (defaultsSet || !MEASUREMENT_ID) return;
  defaultsSet = true;

  pushToDataLayer('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
  pushToDataLayer('js', new Date());
};

const injectScript = () => {
  if (scriptInjected || !MEASUREMENT_ID) return;
  scriptInjected = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

/**
 * Applies the user's consent decision.
 * Grants load + configure GA; denials keep (or make) storage denied.
 */
export const applyConsent = (opts: {
  analytics: boolean;
  marketing: boolean;
}) => {
  if (!MEASUREMENT_ID) return;

  initConsentDefaults();

  const analytics = opts.analytics ? 'granted' : 'denied';
  const marketing = opts.marketing ? 'granted' : 'denied';

  if (opts.analytics) {
    injectScript();

    if (!configured) {
      configured = true;
      pushToDataLayer('config', MEASUREMENT_ID, {
        anonymize_ip: true,
        allow_google_signals: opts.marketing,
        allow_ad_personalization_signals: opts.marketing,
        // We send page_view manually on every route change (SPA)
        send_page_view: false,
      });
    }
  }

  pushToDataLayer('consent', 'update', {
    analytics_storage: analytics,
    functionality_storage: analytics,
    personalization_storage: marketing,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
};

/** True only when GA is configured, consented and running. */
const canTrack = () => Boolean(MEASUREMENT_ID) && configured;

export const gaPageView = (path: string, title?: string) => {
  if (!canTrack()) return;
  pushToDataLayer('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
};

export const gaEvent = (name: string, params?: Record<string, unknown>) => {
  if (!canTrack()) return;
  pushToDataLayer('event', name, params ?? {});
};
